import { z } from 'zod';
import { runStatus, uuid } from './primitives';

/**
 * SSE stream wire contracts (Master Spec §14). The Operations stream carries small
 * routing hints — a client receives an event, then refetches the affected run /
 * approval list from canonical state (I1: SSE is a latency optimisation over
 * polling, never the source of truth). Each event carries a MONOTONIC id; a client
 * reconnects with `Last-Event-ID` and the server replays from that cursor, falling
 * back to a full refetch when the cursor is too old (§14).
 *
 * The event `id` is a monotonic sequence scoped to the SSE endpoint (a per-server
 * counter seeded from the notification/step cursor). `type` selects the SSE event
 * name; `data` is the JSON payload.
 */

/** A run changed state or gained a step — refetch the run detail. */
export const runUpdateEvent = z.object({
  kind: z.literal('run_update'),
  runId: uuid,
  status: runStatus.optional(),
  /** Highest step number known when this event was emitted (append hint). */
  latestStepNo: z.number().int().nonnegative().optional(),
});
export type RunUpdateEvent = z.infer<typeof runUpdateEvent>;

/** An approval was created or decided — refetch the approvals inbox / detail. */
export const approvalUpdateEvent = z.object({
  kind: z.literal('approval_update'),
  approvalId: uuid,
});
export type ApprovalUpdateEvent = z.infer<typeof approvalUpdateEvent>;

/** A web notification is ready — refetch the notification badge. */
export const notificationUpdateEvent = z.object({
  kind: z.literal('notification_update'),
  notificationId: uuid,
});
export type NotificationUpdateEvent = z.infer<typeof notificationUpdateEvent>;

export const streamEvent = z.discriminatedUnion('kind', [
  runUpdateEvent,
  approvalUpdateEvent,
  notificationUpdateEvent,
]);
export type StreamEvent = z.infer<typeof streamEvent>;
